import { useNavigate, useParams } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import metadataGif from '~/assets/img/metadata.gif';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useQuery } from '~/common/routerUtils';
import Icon from '~/components2/Icon';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import {
  EXPORT_HISTORY_TAB_ID,
  EXPORT_HISTORY_TAB_OPEN_SELECTOR,
  EXPORT_TAB_ID,
  EXPORT_TAB_OPEN_SELECTOR,
} from '~/pages/projectDetails/exportTabIds';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import type { ProjectDetailsParams } from '../projectDetails';
import projectDetailsStore from '../projectDetails/store';
import OnboardingMask from './OnboardingMask';
import OnboardingPopup from './OnboardingPopup';

interface Step {
  content: ReactNode;
  mask: ReactNode;
}

export default function ProjectOnboarding() {
  const navigate = useNavigate();
  const { inOnboardingMode, inUnverifiedMode } = useSnapshot(rootStore);
  const { isExpanded, civilSearchVersion } = useSnapshot(projectDetailsStore);
  const labels = useExportLabels();
  const [currentStep, setCurrentStep] = useState<string>('layer-picker-intro');
  const [civildrawIntroReady, setCivildrawIntroReady] = useState(false);
  const { projectId, tabId, mapId } = useParams({ strict: false }) as ProjectDetailsParams;
  const qs = useQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!inOnboardingMode) {
      return;
    }
    if (tabId === undefined) {
      setCurrentStep('layer-picker-intro');
      layerStore.toggleTOC(true);
      layerStore.toggleLegend(true);
    } else if (tabId === 'utilities') {
      if (civilSearchVersion === 'v1') {
        setCurrentStep('utilities-v1');
        layerStore.toggleTOC(false);
        layerStore.toggleLegend(false);
        return;
      }

      layerStore.toggleTOC(false);
      layerStore.toggleLegend(false);
      if (qs.has('drawer')) {
        if (qs.get('drawer') === 'request-data') {
          setCurrentStep('utilities-request');
          return;
        }
      }
      if (isExpanded) {
        setCurrentStep('utilities-detail');
      } else {
        setCurrentStep('utilities');
      }
    } else if (tabId === EXPORT_TAB_ID || tabId === EXPORT_HISTORY_TAB_ID) {
      layerStore.toggleTOC(false);
      layerStore.toggleLegend(false);
      setCurrentStep('download');
    } else if (tabId === 'civil-draw' && mapId) {
      setCurrentStep('civil-draw');
      layerStore.toggleTOC(false);
      layerStore.toggleLegend(false);
    } else {
      setCurrentStep('project-left-nav');
      layerStore.toggleTOC(false);
      layerStore.toggleLegend(false);
    }
  }, [tabId, inOnboardingMode, qs, isExpanded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    // we start at 3 because there are two steps in home onboarding.
    const stepNumber = Object.keys(steps).indexOf(currentStep) + 3;

    capturePosthogEvent(POSTHOG_EVENTS.onboardingStepViewed, {
      step_name: currentStep,
      step_number: stepNumber,
      project_id: projectId,
    });
  }, [currentStep]);

  // When the civildraw-intro step is shown, scroll the LeftNav to the bottom instantly, then allow the popup to find/position on #left-nav-civildraw.
  const civildrawIntroStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentStep !== 'civildraw-intro') {
      setCivildrawIntroReady(false);
      civildrawIntroStepRef.current = null;
      return;
    }
    civildrawIntroStepRef.current = 'civildraw-intro';
    setCivildrawIntroReady(false);
    const scrollContainer = document.querySelector('#project-left-nav .overflow-y-auto');
    if (!scrollContainer) {
      setCivildrawIntroReady(true);
      return;
    }
    scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'auto' });
    const rafId = requestAnimationFrame(() => {
      if (civildrawIntroStepRef.current === 'civildraw-intro') {
        setCivildrawIntroReady(true);
      }
    });
    return () => {
      civildrawIntroStepRef.current = null;
      cancelAnimationFrame(rafId);
    };
  }, [currentStep]);

  // One step covers both export tabs, and each renders its own DOM node — pointing at the other
  // tab's node leaves the mask waiting on an element that never appears.
  const exportTabSelector =
    tabId === EXPORT_HISTORY_TAB_ID ? EXPORT_HISTORY_TAB_OPEN_SELECTOR : EXPORT_TAB_OPEN_SELECTOR;

  const steps: { [id: string]: Step } = {
    'layer-picker-intro': {
      content: (
        <OnboardingPopup
          selector="#layer-picker-open"
          title="Layer List"
          onNext={() => {
            layerStore.toggleTOC(false);
            layerStore.toggleLegend(false);
            setCurrentStep('map-metadata');
          }}
          width="w-[250px]"
        >
          <div className="text-sm">
            CivilGrid contains 150+ datasets to simplify your project planning. Toggle them to view the data on the map
            in the next step.
          </div>
          <br />
          <div className="text-sm">
            Click{' '}
            <a
              href="https://civilgrid.help.usepylon.com/articles/2709610295-data-layers"
              className="text-blue-50 underline hover:text-blue-60"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>{' '}
            to learn more.
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="layer-picker-intro" selector="#layer-picker-open" />,
    },
    'map-metadata': {
      content: (
        <OnboardingPopup
          title="Map"
          onNext={() => setCurrentStep('project-left-nav')}
          width="w-[350px]"
          position={{
            left: 200,
            top: 80,
          }}
        >
          <div className="text-sm">
            Zoom & pan to explore all the datasets on the fully interactive map. Right-click on any layer in the map to
            view Layer Attributes.
          </div>
          <br />
          <div className="text-sm">
            💡 Checkout the legend in the bottom right.
            <img className="mt-3" src={metadataGif} alt="Layer Attributes demonstration" />
          </div>
          <br />
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="map-metadata" selector="#projectDetailsMap" />,
    },
    'project-left-nav': {
      content: (
        <OnboardingPopup selector="#project-left-nav" width="w-[250px]" positionOffset={{ top: 325, left: 0 }}>
          <div className="text-sm">
            Want to see a list of all the utilities in your project area? <br />
            <br />
            Open the <b>Utilities</b> tab.
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="project-left-nav" selector="#project-left-nav" />,
    },
    utilities: {
      content: (
        <OnboardingPopup
          width="w-[250px]"
          title="Utilities - Summary"
          selector="#tab-utilities-open"
          positionOffset={{ top: 150, left: -(550 + 250 + 25) }} // -(tab width + popup width + padding)
          onNext={() => projectDetailsStore.toggleIsExpanded()}
        >
          <div className="text-sm">This is a summary of all the utilities that operate in your project area.</div>

          <div className="my-2 text-sm">
            Want a more in-depth view? Click <b>View Details</b>
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="tab-utilities-summary" selector="#tab-utilities-open" padding={[0, 60]} />,
    },
    'utilities-detail': {
      content: (
        <OnboardingPopup
          width="w-[350px]"
          title="Utilities - Detail"
          position={{ bottom: 10, right: 10 }}
          onNext={() =>
            navigate({
              to: '/projects/$projectId/$tabId',
              params: { projectId, tabId: 'utilities' },
              search: { drawer: 'request-data' },
            })
          }
        >
          <div className="text-sm">
            The detail view provides an in-depth look at the utilities available on our platform. Here are some tips to
            explore:
            <ul className="my-2 list-inside list-disc text-sm">
              <li>
                Move your mouse over any item with a colored background like the green{' '}
                <span className="rounded-sm bg-green-100 px-1 text-green-900">Yes</span>. That will provide more
                information about the item.
              </li>
              <li>
                Hover over any item in the <i>Latest Update</i> column to view additional details about the specific
                data request.
              </li>
            </ul>
          </div>
          <div className="my-2 text-sm">
            What if we don't have data for an agency? No problem, click <b>Request Data</b> above and we can{' '}
            <a
              href="https://civilgrid.help.usepylon.com/articles/7709282608-2-min-video-guide-to-new-utilities-tab-and-records-request-ui"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-50 underline hover:text-blue-60"
            >
              request
            </a>{' '}
            them for you.
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="tab-utilities-detail" selector="#tab-utilities-open" padding={[0, 60]} />,
    },
    'utilities-request': {
      content: (
        <OnboardingPopup
          width="w-[250px]"
          title="Request Data"
          selector="#drawer-open-request-data"
          positionOffset={{ top: 150, left: -(550 + 250 + 25) }} // -(tab width + popup width + padding)
          onNext={() => navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID } })}
        >
          <div className="text-sm">You can choose the agencies for which you need maps and as-builts.</div>

          <div className="my-2 text-sm">
            If an agency charges a map fee or requires additional documentation, we provide that information to you
            right at the start. No surprises!
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="drawer-open-request-data" selector="#drawer-open-request-data" padding={0} />,
    },
    'utilities-v1': {
      content: (
        <OnboardingPopup
          width="w-[250px]"
          title="Utilities"
          selector="#tab-utilities-open"
          positionOffset={{ top: 250, left: -800 }}
          onNext={
            inUnverifiedMode
              ? () => navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID } })
              : undefined
          }
        >
          <div className="text-sm">View all the utilities that operate in your project area.</div>

          <div className="my-2 text-sm">
            What if we don't have data for an agency? No problem, click <b>Request</b> in the <i>Request Maps</i>{' '}
            section and we'll{' '}
            <a
              href="https://civilgrid.help.usepylon.com/articles/7709282608-2-min-video-guide-to-new-utilities-tab-and-records-request-ui"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-50 underline hover:text-blue-60"
            >
              request
            </a>{' '}
            them for you.
          </div>

          <div className="text-sm">
            💡 Want to import them as CAD, KMZ, DGN? Click <b>{labels.onboardingCta}</b>
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="#tab-utilities-open" selector="#tab-utilities-open" padding={10} />,
    },
    download: {
      content: (
        <OnboardingPopup
          selector={exportTabSelector}
          onNext={() => setCurrentStep('civildraw-intro')}
          width="w-[250px]"
          title={labels.onboardingStepTitle}
          positionOffset={{ top: 250, left: -800 }}
        >
          <div className="text-sm">
            You can export utility data directly into formats like DWG, DGN, ShapeFile and KMZ.
          </div>

          <div className="my-2 text-sm">Go ahead and try it out!</div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key={exportTabSelector} selector={exportTabSelector} padding={10} />,
    },
    end: {
      content: (
        <OnboardingPopup
          onNext={() => {
            rootStore.setInOnboardingMode(false);
            navigate({ to: '/home' });
          }}
          title="🎉 All set"
          nextLabel="End"
          showSkip={false}
        >
          <div className="text-sm">
            Congratulations! You're one step closer to simplifying your utility research process!
          </div>
          <br />
          <div className="text-sm">
            <div>Here are some next steps:</div>
            <ol className="list-inside list-disc text-sm">
              <li>
                Setup a{' '}
                <a
                  href="https://calendly.com/sam-civilgrid/civilgrid-15-minute-meeting-w-sam"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-50 underline hover:text-blue-60"
                >
                  15-minute call
                </a>{' '}
                with a product specialist to answer specific questions.
              </li>
              <li>
                Create your{' '}
                <a
                  href="https://civilgrid.help.usepylon.com/articles/2820338851-using-draft-projects"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-50 underline hover:text-blue-60"
                >
                  first draft
                </a>
                , it's FREE!
              </li>
            </ol>
            <div className="mt-4 text-sm">
              If you need assistance or want to take a product tour, click the
              <span className="inline-block px-1">
                <Icon icon="help2" ariaLabel="Help" />
              </span>{' '}
              icon in the top right corner.
            </div>
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="end" />,
    },
    'civildraw-intro': {
      content: (
        <OnboardingPopup selector="#left-nav-civildraw" width="w-[250px]">
          <div className="text-sm">
            There's more! <br />
            <br />
            Ever wanted to sketch on a map to share a quick idea? Click on the first map to see how.
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask key="#left-nav-civildraw" selector="#left-nav-civildraw" padding={10} />,
    },
    'civil-draw': {
      content: (
        <OnboardingPopup title="CivilDraw" onNext={() => setCurrentStep('end')} position={{ left: 200, top: 80 }}>
          <div>
            <div className="text-sm">Let's try this:</div>

            <ol className="list-inside list-decimal text-sm">
              <li>Click on an object, move it and change its name</li>
              <li>Click on the first button in the toolbar at the bottom the page, click anywhere to start drawing</li>
              <li>
                Right-click anywhere on the map and click <b>Open Street View</b>
              </li>
            </ol>
            <br />
            <div className="text-sm">
              💡 You can create unlimited maps using CivilDraw to collaborate with your team.
            </div>
          </div>
        </OnboardingPopup>
      ),
      mask: <OnboardingMask selector="#civildraw-map" />,
    },
  };

  if (!inOnboardingMode) {
    return null;
  }

  // Civildraw intro step is special because it's the only step that requires scrolling the LeftNav to the bottom.
  if (currentStep === 'civildraw-intro') {
    return (
      <>
        <OnboardingPopup
          selector={civildrawIntroReady ? '#left-nav-civildraw' : undefined}
          width="w-[250px]"
          positionOffset={{ top: -100, left: 0 }}
        >
          <div className="text-sm">
            There&apos;s more! <br />
            <br />
            Ever wanted to sketch on a map to share a quick idea? Click on the first map to see how.
          </div>
        </OnboardingPopup>
        <OnboardingMask key="#left-nav-civildraw" selector="#left-nav-civildraw" padding={10} />
      </>
    );
  }

  return (
    <>
      {steps[currentStep].content}
      {steps[currentStep].mask}
    </>
  );
}
